import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { useLocation, useParams } from 'react-router-dom';
import '../../../components/common/tab-section.css';
import { DocumentDetailResponse } from '../../../interfaces/document';
import { getClinicsForSelect } from '../../../lib/clinic';
import referralService from '../../../services/referral';
import { CustomTabs } from '../../common/CustomTabs/CustomTabs';
import { PageHeader } from '../../common/PageHeader/PageHeader';
import { TabPanel } from '../../common/TabPanel/TabPanel';
import PdfPreviewDialog from '../PdfPreviewDialog';
import {
  buildDocumentUrl,
  isPdfDocumentKey,
  openDocumentAsDownload,
} from '../../../utils/referralUpload.utils';
import {
  getDocumentsLibraryUrlWithFilters,
  resolveDocumentTab,
} from '../utils/navigationHelpers';
import { DocumentInformationTab } from './tabs/DocumentInformationTab';
import { PriorAuthDocumentsTab } from './tabs/PriorAuthDocumentsTab';
import { ProcessingDetailsTab } from './tabs/ProcessingDetailsTab';
import { ReferralDocumentsTab } from './tabs/ReferralDocumentsTab';

// Helper functions

/** The first business tab's index: Document Information and Processing Details come first. */
const FIRST_BUSINESS_TAB_INDEX = 2;

const isReferralDocumentType = (documentType: string): boolean =>
  documentType === 'referral-order' || documentType === 'referral';

/**
 * The document types that get a business tab, in tab order: only types with an actual
 * business document (a referral, a prior auth) behind them.
 */
const getBusinessDocumentTypes = (document: DocumentDetailResponse): string[] =>
  Array.from(
    new Set(
      document.detectedDocuments
        ?.filter((dd) =>
          dd.patientDocuments?.some((pd) => {
            if (isReferralDocumentType(dd.documentType)) {
              return pd.referralDocument;
            }
            if (dd.documentType === 'prior-auth' || dd.documentType === 'prior-authorization') {
              return pd.priorAuthDocument;
            }
            // TODO: Add checks for other document types (lab-orders)
            return false;
          }),
        )
        ?.map((dd) => dd.documentType) || [],
    ),
  );

/**
 * The tab a `?tab=` value opens. Numeric values are tab indexes, as the tabs themselves write
 * them. A name (`referrals`, `prior-authorizations`) opens that business tab, whose index
 * depends on which business documents this document has, so a link from elsewhere (an
 * eligibility task, the Referrals list) can open it without fetching the document first. Once
 * the document is loaded, the value is resolved against its tabs by `resolveDocumentTab`.
 * Unknown values, and a name before the document has loaded, open the first tab.
 */
export const resolveTabParam = (
  tab: string | null,
  document: DocumentDetailResponse | null,
): number => {
  if (document) {
    return resolveDocumentTab(
      tab,
      getTabLabels(document).map(({ label }) => label),
    );
  }
  const index = Number(tab || '0');
  return Number.isInteger(index) && index >= 0 ? index : 0;
};

const getTabLabels = (document: DocumentDetailResponse) => {
  const baseTabLabels = [
    {
      label: 'Document Information',
      onClick: () => {},
    },
    {
      label: 'Processing Details',
      onClick: () => {},
    },
  ];

  // Add business tabs for each document type
  const businessTabs = getBusinessDocumentTypes(document).map((docType) => ({
    label: getBusinessTabLabel(docType),
    onClick: () => {},
  }));

  return [...baseTabLabels, ...businessTabs];
};

const getBusinessTabLabel = (documentType: string): string => {
  switch (documentType) {
    case 'referral-order':
    case 'referral':
      return 'Referrals';
    case 'prior-auth':
    case 'prior-authorization':
      return 'Prior Authorizations';
    case 'lab-order':
    case 'lab-orders':
      return 'Lab Orders';
    default:
      return `${documentType} Documents`;
  }
};

const renderBusinessTabs = (
  document: DocumentDetailResponse,
  currentTabIndex: number,
  clinics: { id: number; name: string }[],
  onReferralUpdate?: (referralId: number, success: boolean, message: string) => void,
) => {
  return getBusinessDocumentTypes(document).map((docType, index) => {
    const tabIndex = FIRST_BUSINESS_TAB_INDEX + index;
    const key = `business-tab-${docType}`;

    if (isReferralDocumentType(docType)) {
      return (
        <TabPanel key={key} value={currentTabIndex} index={tabIndex} sx={{ padding: '14px 0' }}>
          <Box className='tab-section'>
            <ReferralDocumentsTab
              document={document}
              clinics={clinics}
              onReferralUpdate={onReferralUpdate}
            />
          </Box>
        </TabPanel>
      );
    }

    if (docType === 'prior-auth' || docType === 'prior-authorization') {
      return (
        <TabPanel key={key} value={currentTabIndex} index={tabIndex} sx={{ padding: '14px 0' }}>
          <Box className='tab-section'>
            <PriorAuthDocumentsTab document={document} />
          </Box>
        </TabPanel>
      );
    }

    // TODO: Add other document type components (LabOrderTab, etc.)
    return (
      <TabPanel key={key} value={currentTabIndex} index={tabIndex} sx={{ padding: '14px 0' }}>
        <Box className='tab-section'>
          <Typography variant='h6'>{getBusinessTabLabel(docType)} - Coming Soon</Typography>
          <Typography variant='body2' color='text.secondary'>
            Component for {docType} documents will be implemented here.
          </Typography>
        </Box>
      </TabPanel>
    );
  });
};

const DocumentDetails: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const location = useLocation();
  const [document, setDocument] = useState<DocumentDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // The last load failed. With a document already on screen it is kept, and marked as possibly
  // out of date, rather than replaced by the skeleton (see `fetchDocument`).
  const [loadFailed, setLoadFailed] = useState(false);

  const [clinics, setClinics] = useState<{ id: number; name: string }[]>([]);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  // The tab as the URL names it: an index, or a business tab's name (`?tab=referrals`). Kept
  // raw and resolved against this document's tabs at render, since which index a business tab
  // lands on depends on what was detected in the document.
  const getTabFromUrl = () => new URLSearchParams(location.search).get('tab');
  const [requestedTab, setRequestedTab] = useState<string | null>(getTabFromUrl());
  const redirect = useRedirect();

  const fetchDocument = useCallback(async () => {
    if (!documentId) {
      return;
    }

    // A refresh keeps the tabs mounted: the skeleton is for the first load only. Swapping a
    // loaded document for the skeleton unmounted the referral tab on every refresh, which reset
    // its "Edit stays off until the saved values are back" guard — and on a failed refresh
    // remounted it on the pre-save values with Edit on, so the next Save reverted the last one.
    // A failed refresh keeps the document it had, says so, and offers a retry.
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await referralService.getDocumentDetail(Number(documentId));
      if (response) {
        setDocument(response);
      } else {
        setLoadFailed(true);
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  const handleReferralUpdate = useCallback(
    (referralId: number, success: boolean, message: string) => {
      if (success) {
        console.log(`Referral ${referralId} updated successfully: ${message}`);
        fetchDocument();
      }
    },
    [fetchDocument],
  );

  useEffect(() => {
    // A different document starts from the skeleton, never from the previous one's values.
    setDocument(null);
    fetchDocument();
  }, [fetchDocument]);

  // The URL picks the tab once per `location.search`, never again on a refetch: a save,
  // discard or Processing Details action reloads the document, and the user stays on the tab
  // they are on. The requested tab is kept raw and resolved at render, so a named tab
  // (`?tab=referrals`) lands on its index once the document that defines it has loaded.
  useEffect(() => {
    setRequestedTab(new URLSearchParams(location.search).get('tab'));
  }, [location.search]);

  useEffect(() => {
    const getClinics = async () => {
      try {
        const clinicsAllowed = await getClinicsForSelect();
        setClinics(
          clinicsAllowed.map((clinic) => ({
            id: clinic.id,
            name: clinic.name,
          })),
        );
      } catch (error) {
        console.error('Error fetching clinics:', error);
      }
    };
    getClinics();
  }, []);

  const pdfUrl = buildDocumentUrl(document?.s3Key);

  // Only a PDF goes into the preview iframe. Referral packages arrive as ZIP or HL7 XML from
  // an external partner's inbox — untrusted input — and an XML carrying an
  // `<?xml-stylesheet?>` processing instruction executes its XSLT when a browser renders it.
  // A `sandbox` attribute would stop that, but it also disables Chrome's PDF viewer, which
  // would break the preview for every faxed referral. So a non-PDF is opened as a download
  // instead, which is what leads-api already serves it as (`Content-Disposition: attachment`).
  const isPdf = isPdfDocumentKey(document?.s3Key);

  const handleViewPDF = () => {
    if (!pdfUrl) return;

    if (!isPdf) {
      openDocumentAsDownload(pdfUrl);
      return;
    }

    setPdfPreviewOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setRequestedTab(String(newValue));
  };

  if (!document && loadFailed && !loading) {
    return (
      <Box mt={4}>
        <Alert
          severity='error'
          action={
            <Button color='inherit' size='small' onClick={fetchDocument}>
              Retry
            </Button>
          }>
          Could not load this document.
        </Alert>
      </Box>
    );
  }

  if (!document) {
    return (
      <Box mt={4}>
        <Box className='tab-section'>
          {/* Header Skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant='text' width={200} height={32} sx={{ mb: 1 }} />
            <Skeleton variant='text' width={300} height={20} />
          </Box>

          {/* Header Action */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Skeleton variant='text' width={180} height={32} sx={{ mb: 1 }} />
              <Skeleton variant='text' width={280} height={20} />
            </Box>
            <Skeleton variant='rectangular' width={120} height={36} />
          </Box>

          {/* Tab Skeletons */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Skeleton variant='text' width={120} height={24} />
              <Skeleton variant='text' width={140} height={24} />
              <Skeleton variant='text' width={130} height={24} />
            </Box>
          </Box>

          {/* Content Cards */}
          {[1, 2, 3].map((item) => (
            <Card key={item} sx={{ mb: 2 }}>
              <CardContent>
                <Skeleton variant='text' width='40%' height={24} sx={{ mb: 2 }} />
                <Skeleton variant='text' width='100%' height={20} sx={{ mb: 1 }} />
                <Skeleton variant='text' width='80%' height={20} sx={{ mb: 1 }} />
                <Skeleton variant='text' width='60%' height={20} />
              </CardContent>
            </Card>
          ))}

          {/* Loading indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: '12px' }}>
              Loading document details...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const tabLabels = getTabLabels(document);
  const currentTabIndex = resolveTabParam(requestedTab, document);

  return (
    <Box mt={4} className='tab-section-table'>
      <Title title={`Document ID: ${document.externalId || document.documentId}`} />

      {loading && <LinearProgress aria-label='Reloading the document' sx={{ mb: 1 }} />}
      {loadFailed && !loading && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button color='inherit' size='small' onClick={fetchDocument}>
              Retry
            </Button>
          }>
          Could not reload this document, so what is shown may be out of date. Editing a referral
          stays off after a save until the reload succeeds.
        </Alert>
      )}

      <CustomTabs
        tabLabels={tabLabels}
        value={currentTabIndex}
        onChange={handleTabChange}
      />

      <TabPanel value={currentTabIndex} index={0} key={'document-info'} sx={{ padding: '14px 0' }}>
        <Box className='tab-section'>
          <PageHeader
            showBackButton={true}
            onBack={() => redirect(getDocumentsLibraryUrlWithFilters(location.search))}
            backButtonLabel='Back to Library'
            breadcrumbs={[
              {
                label: 'Documents',
                onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
              },
              {
                label: 'Library',
                onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
              },
              { label: `${document.externalId || document.documentId}`, active: true },
            ]}
            actions={
              document.s3Key && (
                <Button
                  variant='contained'
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleViewPDF}
                  sx={{ fontSize: '12px', textTransform: 'capitalize' }}>
                  {isPdf ? 'View PDF' : 'Download file'}
                </Button>
              )
            }
          />

          <DocumentInformationTab document={document} />
        </Box>
      </TabPanel>

      <TabPanel
        value={currentTabIndex}
        index={1}
        key={'processing-details'}
        sx={{ padding: '14px 0' }}>
        <Box className='tab-section'>
          <ProcessingDetailsTab document={document} onActionComplete={fetchDocument} />
        </Box>
      </TabPanel>

      {renderBusinessTabs(document, currentTabIndex, clinics, handleReferralUpdate)}

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        url={pdfUrl}
        title={document.externalId || `Document ${document.documentId}`}
        onClose={() => setPdfPreviewOpen(false)}
      />
    </Box>
  );
};

export default DocumentDetails;
